import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronRight } from 'lucide-react';

const SelectorContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    border-color: var(--primary);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const SelectedAccount = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProfilePicture = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 8px 16px rgba(165, 145, 239, 0.3);
`;

const TypeGroup = styled.div`
  border-bottom: 1px solid var(--card-border);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TypeHeader = styled.div`
  padding: 12px;
  background: var(--surface);
  color: var(--primary);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: var(--card-border);
  }
`;

const AccountOption = styled.div`
  padding: 10px 12px 10px 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-secondary);
  
  &:hover {
    background: var(--card-border);
    color: var(--text-primary);
  }
  
  ${props => props.$selected && `
    background: var(--primary);
    color: var(--text-primary);
  `}
`;

const AccountInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const AccountUsername = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const AccountMeta = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$connected ? 'var(--success)' : 'var(--error)'};
`;

const AccountSelector = ({ accounts, value, onChange, label = "Compte Instagram" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState({});
  
  // Grouper les comptes par type
  const groupedAccounts = accounts.reduce((acc, account) => {
    const type = account.account_type || 'Autre';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(account);
    return acc;
  }, {});
  
  // Ouvrir tous les groupes par défaut
  useEffect(() => {
    const types = Object.keys(groupedAccounts);
    const expanded = {};
    types.forEach(type => expanded[type] = true);
    setExpandedTypes(expanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);
  
  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest('[data-account-selector]')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  const selectedAccount = accounts.find(acc => acc.id === value);
  
  const toggleType = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  
  const handleSelect = (accountId) => {
    onChange(accountId);
    setIsOpen(false);
  };
  
  return (
    <SelectorContainer data-account-selector>
      <Label>{label}</Label>
      <SelectWrapper>
        <SelectButton onClick={() => setIsOpen(!isOpen)}>
          {selectedAccount ? (
            <SelectedAccount>
              {selectedAccount.profile_pic_url && (
                <ProfilePicture 
                  src={selectedAccount.profile_pic_url} 
                  alt={selectedAccount.username}
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div>
                @{selectedAccount.username}
                {selectedAccount.device_connected && <StatusDot $connected />}
              </div>
            </SelectedAccount>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>-- Choisir un compte --</span>
          )}
          <ChevronDown size={16} />
        </SelectButton>
        
        {isOpen && (
          <Dropdown>
            {Object.entries(groupedAccounts).map(([type, typeAccounts]) => (
              <TypeGroup key={type}>
                <TypeHeader onClick={() => toggleType(type)}>
                  {expandedTypes[type] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  {type} ({typeAccounts.length})
                </TypeHeader>
                
                {expandedTypes[type] && typeAccounts.map(account => (
                  <AccountOption
                    key={account.id}
                    $selected={account.id === value}
                    onClick={() => handleSelect(account.id)}
                  >
                    {account.profile_pic_url && (
                      <ProfilePicture 
                        src={account.profile_pic_url} 
                        alt={account.username}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <AccountInfo>
                      <AccountUsername>@{account.username}</AccountUsername>
                      <AccountMeta>
                        <StatusDot $connected={account.device_connected} />
                        {account.device_connected ? 'Connecté' : 'Déconnecté'}
                        {account.followers_count && (
                          <span>• {account.followers_count.toLocaleString()} followers</span>
                        )}
                      </AccountMeta>
                    </AccountInfo>
                  </AccountOption>
                ))}
              </TypeGroup>
            ))}
          </Dropdown>
        )}
      </SelectWrapper>
    </SelectorContainer>
  );
};

export default AccountSelector;
